/**
 * A Performance Matrix row — the supplier over every site in view — and, once
 * opened, one row per customer site beneath it. Customer review 11 Sep (review
 * msg 3/4/17).
 *
 * The Customer cell names the sites the grades cover ("All sites (N)", the list
 * and codes on hover); there is no top-level Supplier Code. A site row is the
 * SAME matrix request with the view scope replaced by that site and narrowed to
 * this supplier, so its grades come from the same engine as the row above.
 *
 * Msg 17: a month cell no longer navigates to the supplier page — "a click
 * through that doesn't mean anything to the user". The supplier name is the
 * one link, and says where it goes.
 */
import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown, ChevronRight, Loader2 } from 'lucide-react';
import { suppliersAPI, MatrixRatingBand, PerformanceMatrixRow } from '../../../lib/api';
import { useFetchData } from '../../../hooks/useFetchData';
import { codeLabel, customerScopeLabel } from '../utils';
import { MatrixRatingCell } from './MatrixRatingCell';
import { MatrixParams } from './usePerformanceMatrix';

interface MatrixRowProps {
  row: PerformanceMatrixRow;
  monthKeys: readonly string[];
  bands: MatrixRatingBand[];
  qualityLabel: string;
  variant?: 'supplier' | 'site';
  /** CR-7288: every code that site holds, not the primary alone. */
  siteCodes?: string[];
  expandable?: boolean;
  expanded?: boolean;
  onToggle?: () => void;
}

function MatrixRow({
  row, monthKeys, bands, qualityLabel, variant = 'supplier', siteCodes, expandable, expanded, onToggle,
}: MatrixRowProps) {
  const isSite = variant === 'site';
  const scope = customerScopeLabel(row.customer_sites);
  // Solid: the frozen Supplier cell sits over the scrolled month columns.
  const bg = isSite ? 'bg-slate-50 dark:bg-slate-900' : 'bg-background';
  return (
    <tr className={`border-b transition-colors ${isSite ? 'bg-slate-50 dark:bg-slate-900' : 'hover:bg-muted/30'}`}
        data-testid={isSite ? `matrix-site-row-${row.supplier_id}` : `matrix-row-${row.supplier_id}`}>
      <td className={`px-3 py-2.5 sticky left-0 ${bg} z-10`}>
        {isSite ? (
          <div className="pl-7">
            <span className="text-sm text-muted-foreground block truncate max-w-[210px]">{row.Supplier}</span>
            {codeLabel(siteCodes) && (
              <span className="text-xs text-muted-foreground">{codeLabel(siteCodes)}</span>
            )}
          </div>
        ) : (
          <div className="flex items-center gap-2">
            {expandable ? (
              <button
                type="button"
                onClick={onToggle}
                aria-expanded={expanded}
                aria-label={expanded ? `Hide ${row.Supplier} by customer site` : `Show ${row.Supplier} by customer site`}
                title={expanded ? 'Hide the per-site breakdown' : 'Show grades at each customer site'}
                className="h-5 w-5 shrink-0 inline-flex items-center justify-center rounded hover:bg-muted"
              >
                {expanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
              </button>
            ) : (
              <span className="w-5 shrink-0" />
            )}
            <Link
              to={`/suppliers/${row.supplier_id}`}
              className="text-sm truncate max-w-[200px] block hover:underline"
              title={`${row.Supplier} — open the supplier profile`}
            >
              {row.Supplier}
            </Link>
          </div>
        )}
      </td>
      <td className="px-3 py-2.5" title={isSite ? undefined : scope.title}>
        <span className="text-sm block" style={{ wordWrap: 'break-word', maxWidth: '200px' }}>
          {row.Business || '-'}
        </span>
        {!isSite && codeLabel(row.SupplierCodes) && (
          <span className="text-[11px] text-muted-foreground">{codeLabel(row.SupplierCodes)}</span>
        )}
      </td>
      {monthKeys.map((m) => (
        <td key={m} className="px-2 py-2.5 text-center">
          <MatrixRatingCell
            rating={row[m as keyof PerformanceMatrixRow] as string}
            bands={bands}
            pct={row[`${m}_pct` as keyof PerformanceMatrixRow] as number | null}
            score={row[`${m}_score` as keyof PerformanceMatrixRow] as number | null | undefined}
            ppm={row[`${m}_ppm` as keyof PerformanceMatrixRow] as number | null | undefined}
            qualityLabel={qualityLabel}
          />
        </td>
      ))}
      <td className="px-2 py-2.5 text-center border-l">
        <MatrixRatingCell
          rating={row.OverallRating}
          bands={bands}
          pct={row.OverallPct}
          score={row.OverallScore}
          ppm={row.OverallPpm}
          qualityLabel={qualityLabel}
        />
      </td>
    </tr>
  );
}

interface MatrixSupplierRowsProps {
  row: PerformanceMatrixRow;
  monthKeys: readonly string[];
  bands: MatrixRatingBand[];
  qualityLabel: string;
  year: number;
  requestParams: MatrixParams;
  expanded: boolean;
  onToggle: () => void;
}

export function MatrixSupplierRows({
  row, monthKeys, bands, qualityLabel, year, requestParams, expanded, onToggle,
}: MatrixSupplierRowsProps) {
  const sites = row.customer_sites ?? [];
  const expandable = sites.length > 1;
  const columnCount = 3 + monthKeys.length;
  const { data, loading, error, refetch } = useFetchData<Array<{ siteId: number; codes: string[]; row: PerformanceMatrixRow | null }>>(
    async () => {
      if (!expanded) return [];
      const base: MatrixParams = { ...requestParams };
      delete base['group'];
      delete base['company'];
      delete base['exclude_na_ratings'];
      const responses = await Promise.all(
        sites.map((s) => suppliersAPI.getPerformanceMatrix({ ...base, year, site: s.id, company_id: row.supplier_id })),
      );
      return sites.map((s, i) => ({
        siteId: s.id,
        codes: s.supplier_codes,
        row: responses[i].data.rows.find((r) => r.supplier_id === row.supplier_id) ?? null,
      }));
    },
    { initialLoading: false, deps: [expanded, row.supplier_id, year, JSON.stringify(requestParams)] },
  );

  const note = (text: ReactNode, key: string) => (
    <tr key={key} className="border-b bg-slate-50 dark:bg-slate-900">
      <td colSpan={columnCount} className="px-3 py-2 pl-12 text-xs text-muted-foreground">{text}</td>
    </tr>
  );

  return (
    <>
      <MatrixRow
        row={row} monthKeys={monthKeys} bands={bands} qualityLabel={qualityLabel}
        expandable={expandable} expanded={expanded} onToggle={onToggle}
      />
      {expanded && loading && note(
        <span className="inline-flex items-center gap-2"><Loader2 className="h-3.5 w-3.5 animate-spin" />Loading {row.Supplier} at each of {sites.length} customer sites…</span>,
        'loading',
      )}
      {expanded && !loading && error && note(
        <span className="text-red-600">
          Could not load the per-site grades: {error}{' '}
          <button type="button" className="underline" onClick={() => refetch()}>Retry</button>
        </span>,
        'error',
      )}
      {expanded && !loading && !error && (data ?? []).map((entry) =>
        entry.row ? (
          <MatrixRow
            key={`site-${entry.siteId}`}
            row={entry.row} monthKeys={monthKeys} bands={bands} qualityLabel={qualityLabel}
            variant="site" siteCodes={entry.codes}
          />
        ) : (
          note(`${sites.find((s) => s.id === entry.siteId)?.name}: no graded deliveries for ${row.Supplier} in this year`, `empty-${entry.siteId}`)
        ),
      )}
    </>
  );
}
